/**
 * TalentExpensesPanel — submit + list reimbursement requests for a booking.
 * Drop-in: <TalentExpensesPanel bookingId={...} bookingStatus={...} />
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Receipt, Loader2, Plus, Trash2, FileText, CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { value: 'parking', label: 'Parking' },
  { value: 'travel', label: 'Travel / Mileage' },
  { value: 'tolls', label: 'Tolls' },
  { value: 'meals', label: 'Meals' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'other', label: 'Other' },
];

const STATUS_BADGE = {
  pending:   { className: 'bg-amber-100 text-amber-800 border-amber-300',  icon: Clock,         label: 'Pending'   },
  approved:  { className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, label: 'Approved'  },
  rejected:  { className: 'bg-red-100 text-red-800 border-red-300',         icon: XCircle,      label: 'Rejected'  },
  cancelled: { className: 'bg-gray-100 text-gray-700 border-gray-300',      icon: XCircle,      label: 'Cancelled' },
};

export default function TalentExpensesPanel({ bookingId, bookingStatus, onChange }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    expense_type: 'parking', amount: '', notes: '', receipt_url: null,
  });

  const canSubmit = ['confirmed', 'checked_in', 'completed'].includes(bookingStatus);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/talent/bookings/${bookingId}/expenses`);
      setExpenses(r.data || []);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/expenses/upload-receipt?booking_id=${bookingId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, receipt_url: r.data.storage_path }));
      toast.success('Receipt attached');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      await api.post(`/talent/bookings/${bookingId}/expenses`, {
        expense_type: form.expense_type,
        amount: amt,
        notes: form.notes?.trim() || null,
        receipt_url: form.receipt_url || null,
      });
      toast.success('Expense submitted for review');
      setForm({ expense_type: 'parking', amount: '', notes: '', receipt_url: null });
      setShowForm(false);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelExpense = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Cancel this expense?')) return;
    try {
      await api.delete(`/talent/expenses/${id}`);
      toast.success('Expense cancelled');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const total = (expenses || [])
    .filter((e) => e.status === 'approved')
    .reduce((s, e) => s + (e.effective_amount || 0), 0);

  return (
    <Card data-testid="talent-expenses-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className="w-4 h-4 text-cyan-600" /> Reimbursements
          {total > 0 && (
            <Badge className="ml-2 bg-emerald-100 text-emerald-800 border border-emerald-300">
              ${total.toFixed(2)} approved
            </Badge>
          )}
        </CardTitle>
        {canSubmit && !showForm && (
          <Button
            size="sm"
            onClick={() => setShowForm(true)}
            data-testid="add-expense-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {showForm && (
          <div className="rounded border border-dashed border-cyan-300 p-3 space-y-2 bg-cyan-50/30" data-testid="expense-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Type</Label>
                <Select
                  value={form.expense_type}
                  onValueChange={(v) => setForm({ ...form, expense_type: v })}
                >
                  <SelectTrigger data-testid="expense-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount ($)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Brief description"
                data-testid="expense-notes-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs cursor-pointer inline-flex items-center gap-1.5 px-2 py-1 border rounded hover:bg-white">
                <FileText className="w-3.5 h-3.5" />
                {form.receipt_url ? 'Receipt attached' : 'Upload receipt (PDF/JPG/PNG)'}
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  className="hidden"
                  onChange={(e) => uploadReceipt(e.target.files?.[0])}
                  data-testid="expense-receipt-input"
                />
              </label>
              {uploading && <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setForm({ expense_type: 'parking', amount: '', notes: '', receipt_url: null }); }}>Cancel</Button>
              <Button size="sm" onClick={submit} disabled={submitting || uploading} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="submit-expense-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for review'}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> loading…</div>
        ) : expenses.length === 0 ? (
          <p className="text-xs text-gray-500">No expenses submitted yet.</p>
        ) : (
          <ul className="space-y-2">
            {expenses.map((e) => {
              const cfg = STATUS_BADGE[e.status] || STATUS_BADGE.pending;
              const Icon = cfg.icon;
              return (
                <li key={e.id} className="flex items-start justify-between gap-2 text-sm border rounded px-3 py-2" data-testid={`expense-row-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium capitalize">{e.expense_type}</span>
                      <span className="font-semibold">${e.effective_amount?.toFixed(2)}</span>
                      {e.adjusted_amount != null && e.adjusted_amount !== e.amount && (
                        <span className="text-[11px] text-gray-500 line-through">${e.amount?.toFixed(2)}</span>
                      )}
                      <Badge variant="outline" className={`text-[10px] ${cfg.className}`} data-testid={`expense-status-${e.id}`}>
                        <Icon className="w-3 h-3 mr-0.5 inline" />{cfg.label}
                      </Badge>
                      {e.paid_out_at && (
                        <Badge className="text-[10px] bg-emerald-600 text-white">Paid</Badge>
                      )}
                    </div>
                    {e.notes && <p className="text-[11px] text-gray-600 mt-0.5">{e.notes}</p>}
                    {e.review_notes && <p className="text-[11px] text-amber-700 mt-0.5"><b>Admin:</b> {e.review_notes}</p>}
                    {e.receipt_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/expenses/receipt/${e.receipt_url}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] text-cyan-700 hover:underline inline-flex items-center gap-1 mt-0.5"
                      >
                        <FileText className="w-3 h-3" /> View receipt
                      </a>
                    )}
                  </div>
                  {e.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => cancelExpense(e.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Cancel"
                      data-testid={`expense-cancel-${e.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
